// Screen Wake Lock hook.
//
// Holds a `screen` wake lock while `enabled` is true and the document
// is visible. Combines two strategies:
//
// 1. Screen Wake Lock API (iOS 16.4+, Android Chrome, desktop Chrome/Edge/Safari)
//    Auto-released by OS on tab hide; re-acquired on visibilitychange.
//
// 2. Video fallback for iOS PWA / older Safari where the API isn't available
//    or silently fails. A 1×1 transparent looping video element prevents the
//    display from sleeping — the same technique used by mapping apps to keep
//    screens on during navigation.
//
// Wired into AppShell. Always enabled — the app is a flight tool and should
// never let the iPad sleep while it's open.

import { useEffect } from 'react';

interface WakeLockSentinel extends EventTarget {
  released: boolean;
  release(): Promise<void>;
}

interface WakeLockNavigator {
  wakeLock?: {
    request(type: 'screen'): Promise<WakeLockSentinel>;
  };
}

// Minimal 1×1 transparent video loop encoded as a data URI.
// Playing a video (even silent, invisible) prevents iOS from sleeping.
const TINY_VIDEO_SRC =
  'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAA' +
  'rptZGF0AAACrqQAAAr3AExhdmM1OC4xMDcuMTAwAAABAAAACC9hdmMxAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADhAA' +
  'ACQavcC8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

let videoEl: HTMLVideoElement | null = null;

function getOrCreateVideo(): HTMLVideoElement {
  if (videoEl) return videoEl;
  videoEl = document.createElement('video');
  videoEl.setAttribute('playsinline', '');
  videoEl.setAttribute('muted', '');
  videoEl.setAttribute('loop', '');
  videoEl.setAttribute('webkit-playsinline', '');
  videoEl.style.cssText = 'position:fixed;top:-1px;left:-1px;width:1px;height:1px;opacity:0;pointer-events:none;';
  videoEl.src = TINY_VIDEO_SRC;
  document.body.appendChild(videoEl);
  return videoEl;
}

export function useScreenWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const nav = navigator as Navigator & WakeLockNavigator;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;
    let usingVideoFallback = false;

    async function acquire() {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;

      // Try native Wake Lock API first
      if (nav.wakeLock) {
        try {
          sentinel = await nav.wakeLock.request('screen');
          return; // success — don't need video fallback
        } catch (err) {
          // NotAllowedError on iOS PWA or unfocused tab — fall through to video
          console.warn('[wake-lock] API failed, using video fallback:', err);
        }
      }

      // Video fallback for iOS PWA / older Safari
      usingVideoFallback = true;
      try {
        const vid = getOrCreateVideo();
        await vid.play();
      } catch {
        // Autoplay blocked — nothing we can do without a user gesture
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        if (usingVideoFallback) {
          void getOrCreateVideo().play().catch(() => {});
        } else if (!sentinel || sentinel.released) {
          void acquire();
        }
      } else {
        // Pause video when hidden to save battery; wake lock auto-releases
        if (usingVideoFallback && videoEl) {
          videoEl.pause();
        }
      }
    }

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (sentinel && !sentinel.released) {
        void sentinel.release().catch(() => {});
      }
      if (usingVideoFallback && videoEl) {
        videoEl.pause();
      }
      sentinel = null;
    };
  }, [enabled]);
}
